import React from "react";
import { Box, HStack, Text, Progress, SimpleGrid } from "@chakra-ui/react";

/**
 * The gauges above the DB health checks: how much of each limit (connections,
 * memory, CPU, disk) is in use right now, and the day's peak where the backend
 * records one. The numbers and each gauge's colour come from the backend
 * (api/db_health/checks.py usage_meters): a gauge is coloured by the verdict
 * of the check that judges it, so the two never disagree.
 */

const SCHEME = { problem: "red", warning: "orange", unknown: "gray", ok: "green" };

/** "Connections 35% (24 h peak 88%), Memory 61%" for the plain-text summary. */
export function usageText(usage) {
  return (usage || [])
    .map((m) => `${m.label} ${m.pct}%${m.peak ? ` (24 h peak ${m.peak.pct}%)` : ""}`)
    .join(", ");
}

function Meter({ meter }) {
  const scheme = SCHEME[meter.status] || "gray";
  const peak = meter.peak;
  return (
    <Box borderWidth="1px" borderRadius="md" p={3} data-testid={`db-health-meter-${meter.id}`} data-status={meter.status}>
      <HStack justify="space-between" align="baseline">
        <Text fontSize="sm" fontWeight="semibold">{meter.label}</Text>
        <Text fontSize="xl" fontWeight="bold" color={`${scheme}.600`}>{meter.pct}%</Text>
      </HStack>
      {/* A CPU can be asked for more than it has; the bar stops at full. */}
      <Progress value={Math.min(meter.pct, 100)} size="sm" borderRadius="full" colorScheme={scheme} my={1}
                aria-label={`${meter.label} in use`} />
      <Text fontSize="xs" color="gray.600">{meter.detail}</Text>
      {peak && (
        <Text fontSize="xs" mt={1} data-testid={`db-health-peak-${meter.id}`}>
          <Text as="span" fontWeight="bold" color={`${SCHEME[peak.status] || "gray"}.600`}>24 h peak {peak.pct}%</Text>
          <Text as="span" color="gray.600"> · {peak.detail}</Text>
        </Text>
      )}
    </Box>
  );
}

export default function UsageMeters({ usage }) {
  if (!usage?.length) return null;
  return (
    <Box data-testid="db-health-usage">
      <Text fontSize="sm" fontWeight="bold" color="gray.600" mb={1}>How close it is to its limits right now</Text>
      <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} spacing={3}>
        {usage.map((m) => <Meter key={m.id} meter={m} />)}
      </SimpleGrid>
    </Box>
  );
}
